import React from 'react';

import {assert} from 'Asserts';
import {
  ContainingSchemaTagType,
  isAffiliatedSchemaTag,
  isSchemaFieldDatabaseBacked,
  SchemaFieldDataType,
  SchemaTag,
} from 'folder-items/FileConstants';
import * as FileConstants from 'folder-items/FileConstants';
import TagValueViewer from 'metadata/TagValueViewer';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {
  convertTagToTypedValue,
  convertTypedValueToTag,
  convertTypedValueToUnaffiliatedTag,
} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {getStore} from 'redux-stores/Store';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import BooleanValueCellEditor from 'schemas/tags/BooleanValueCellEditor';
import * as TagUtil from 'schemas/tags/TagUtil';
import {ElementKeyboardEvent, Optional} from 'util/Types';

import InlineCellTagValueEditor from './InlineCellTagValueEditor';
import TagValueInputModal from './TagValueInputModal';

const {FIELD_TYPES} = SchemaFieldConstants;
export const INLINE_TAG_TYPES = [
  FIELD_TYPES.AA_PART_LINK.type,
  FIELD_TYPES.BASE_MOLECULE_LINK.type,
  FIELD_TYPES.BATCH_LINK.type,
  FIELD_TYPES.ENTRY_LINK.type,
  FIELD_TYPES.BIOENTITY_LINK.type,
  FIELD_TYPES.PART_LINK.type,
  FIELD_TYPES.STORABLE_LINK.type,
  FIELD_TYPES.TRANSCRIPTION_LINK.type,
  FIELD_TYPES.TRANSLATION_LINK.type,
  FIELD_TYPES.SELECTOR.type,
  FIELD_TYPES.DATETIME.type,
  FIELD_TYPES.DATE.type,
];

interface TagValueTypedValueInputProps<T extends SchemaField | SchemaFieldDataType> {
  cell: Cell;
  initialQuery?: string;
  query?: string;
  typedValue?: TypedValue | null;
  onBlur?: () => void;
  onEscape: () => void;
  onSetCellData: (
    value: Optional<string>,
    cellData: {typedValue: TypedValue | null},
    enterKey?: boolean
  ) => void;
  onTabKey?: (e: ElementKeyboardEvent) => void;
  onUpdateQuery: (query: string) => void;
  onUpdateTypedValue: (typedValue: TypedValue) => void;
  schemaField: T;
  autoFocus?: boolean;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
  areChipsClickable?: boolean;
}

/**
 * When an entity is selected via autocomplete, the dropdown stores `item.name` (the raw entity
 * name) in targetFiles. But when the same entity is resolved via string resolution (paste + blur),
 * `displayLabel` is used instead — which may be the registry ID depending on schema config. This
 * function aligns autocomplete selections with string resolution by looking up each target file's
 * displayLabel from the Redux store and patching it in.
 */
export function coerceTargetFilesToDisplayLabel(tagValue: SchemaTag, linksKey: 'targetFiles'): SchemaTag {
  const targetFiles = tagValue[linksKey];
  if (!targetFiles || targetFiles.length === 0) {
    return tagValue;
  }
  const {fileSummariesById} = getStore().getState().models;
  let changed = false;
  const updatedFiles = targetFiles.map((file) => {
    if (!file.id) {
      return file;
    }
    const summary = fileSummariesById[file.id];
    if (summary?.displayLabel && summary.displayLabel !== file.name) {
      changed = true;
      return {...file, name: summary.displayLabel};
    }
    return file;
  });
  if (!changed) {
    return tagValue;
  }
  return {...tagValue, [linksKey]: updatedFiles};
}

/**
 * Component that allows the user to input a Typed Value that corresponds to a Tag Value into a table cell.
 */
export default class TagValueTypedValueInput<
  T extends SchemaField | SchemaFieldDataType,
> extends React.Component<TagValueTypedValueInputProps<T>> {
  /**
   * Translate tagValue to typedValue for use in the cell JSON. The formats are nearly identical,
   * but typedValue does not contain a reference to the SchemaField (because it is conceptually
   * independent of its field).
   */
  _handleSetTagValue = (tagValue: SchemaTag, enterKey: boolean): void => {
    const fieldType = TagUtil.getFullFieldType(tagValue.tagSchemaField.fieldType);
    const linksKey = TagUtil.getLinksKeyFromFieldType(fieldType.type);
    if (
      // Fallback to cell.text if the tagValue is not well-formed.
      // TODO(noters): This should be generalized.
      (linksKey && !TagUtil.tagHasLink(tagValue)) ||
      (fieldType === SchemaFieldConstants.FIELD_TYPES.DATETIME && !tagValue.datetimeValue) ||
      (fieldType === SchemaFieldConstants.FIELD_TYPES.DATE && !tagValue.dateValue)
    ) {
      this.props.onSetCellData(tagValue.value, {typedValue: null}, enterKey);
      return;
    }
    const coercedTagValue =
      linksKey === 'targetFiles' ? coerceTargetFilesToDisplayLabel(tagValue, linksKey) : tagValue;
    // To maintain the paradigm of either persisting a text value or a tagValue in a cell but not both, set
    // the text value as empty string if a date or datetime tagValue has valid value
    this.props.onSetCellData(
      [SchemaFieldConstants.FIELD_TYPES.DATETIME, SchemaFieldConstants.FIELD_TYPES.DATE].includes(fieldType)
        ? ''
        : coercedTagValue.value,
      {
        typedValue: convertTagToTypedValue(coercedTagValue),
      },
      enterKey
    );
  };

  _handleUpdateControlledTagValue = (tagValue: SchemaTag): void => {
    const fieldType = TagUtil.getFullFieldType(tagValue.tagSchemaField.fieldType);
    const linksKey = TagUtil.getLinksKeyFromFieldType(fieldType.type);
    const coercedTagValue =
      linksKey === 'targetFiles' ? coerceTargetFilesToDisplayLabel(tagValue, linksKey) : tagValue;
    this.props.onUpdateTypedValue(convertTagToTypedValue(coercedTagValue));
  };

  /**
   * Translate typedValue from the cell to tagValue for the lower-level components.
   */

  _getTagValue(typedValue: Optional<TypedValue>): ContainingSchemaTagType<T> {
    if (typedValue != null) {
      const tagValue = isSchemaFieldDatabaseBacked(this.props.schemaField)
        ? convertTypedValueToTag(typedValue, this.props.schemaField)
        : convertTypedValueToUnaffiliatedTag(typedValue, this.props.schemaField);
      if (tagValue) {
        return tagValue as ContainingSchemaTagType<T>;
      }
    }
    // typedValue was either missing or the wrong type.
    return TagUtil.getInitialTag(this.props.schemaField);
  }

  override render(): JSX.Element {
    const {onEscape, schemaField} = this.props;
    const tagValue = this._getTagValue(this.props.cell.typedValue);
    if (INLINE_TAG_TYPES.includes(schemaField.fieldType)) {
      const tagValueWithInitialQuery = {
        ...TagUtil.getInitialTag(this.props.schemaField),
        value: this.props.initialQuery,
      };
      return (
        <InlineCellTagValueEditor
          onBlur={this.props.onBlur}
          onEscape={onEscape}
          onSetTagValue={this._handleSetTagValue}
          onTabKey={this.props.onTabKey}
          onUpdateTagValue={this._handleUpdateControlledTagValue}
          query={this.props.query || ''}
          onUpdateQuery={this.props.onUpdateQuery}
          tagValue={(() => {
            if (this.props.typedValue) {
              return this._getTagValue(this.props.typedValue);
            } else if (this.props.initialQuery) {
              return tagValueWithInitialQuery;
            } else {
              return tagValue;
            }
          })()}
          autoFocus={this.props.autoFocus}
          isOutsideTable={this.props.isOutsideTable}
          areChipsClickable={this.props.areChipsClickable}
        />
      );
    } else if (schemaField.fieldType === FIELD_TYPES.BOOLEAN.type) {
      const handleSetControlledTagValue = (partialTagValue: Partial<FileConstants.SchemaTag>): void => {
        this.props.onUpdateTypedValue(
          convertTagToTypedValue({
            ...tagValue,
            ...partialTagValue,
          })
        );
      };
      return <BooleanValueCellEditor onUpdate={handleSetControlledTagValue} value={tagValue.booleanValue} />;
    } else {
      assert(
        isSchemaFieldDatabaseBacked(schemaField) && isAffiliatedSchemaTag(tagValue),
        'Unexpectedly used a TagValueInputModal on an unaffiliated schema field',
        {schemaField}
      );
      return (
        <>
          <TagValueInputModal
            onEscape={onEscape}
            onSetTagValue={this._handleSetTagValue}
            schemaField={schemaField}
            tagValue={tagValue}
          />
          <TagValueViewer tag={tagValue} />
        </>
      );
    }
  }
}
